import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { locations } from '../data/locations.ts';
import LazyImage from './LazyImage.tsx';

gsap.registerPlugin(ScrollTrigger);

const LocationsSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, var(--navy-blue) 0%, var(--dark-navy) 50%, var(--navy-blue) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(58, 174, 171, 0.10) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(58, 174, 171, 0.10) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  color: #e3e3dd;
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  .highlight {
    color: #e3e3dd;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e3e3dd;
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const LocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 28px;

  /* En pantallas grandes, fuerza 4 columnas para que entren los 4 locales en una sola fila */
  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (min-width: 992px) and (max-width: 1199px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const LocationCard = styled.div`
  background: #e3e3dd;
  border: 1px solid rgba(1, 74, 144, 0.14);
  border-radius: 25px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 25px 50px rgba(1, 74, 144, 0.18);
    border-color: var(--primary-turquoise);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent, rgba(58, 174, 171, 0.08));
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const LocationImageContainer = styled.div`
  height: 220px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* Overlay MUY sutil (antes era muy oscuro/azul y dominaba el diseño) */
    background: linear-gradient(transparent, rgba(1, 58, 112, 0.18));
    z-index: 2;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(58, 174, 171, 0.08), rgba(1, 74, 144, 0.08));
    z-index: 1;
    pointer-events: none;
  }
`;

const LocationContent = styled.div`
  padding: 30px;
`;

const LocationName = styled.h3`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--navy-blue);
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  margin-bottom: 10px;
  line-height: 1.3;
`;

const LocationDistrict = styled.div`
  display: inline-block;
  background: var(--primary-turquoise);
  color: #e3e3dd;
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
`;

const LocationDescription = styled.p`
  color: rgba(1, 74, 144, 0.85);
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 20px;
  opacity: 0.9;
`;

const LocationDetails = styled.div`
  margin-bottom: 25px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: rgba(1, 74, 144, 0.9);
  font-size: 0.9rem;
  
  i {
    color: var(--primary-turquoise);
    width: 20px;
    margin-right: 10px;
  }

  a {
    color: inherit;
    text-decoration: none;
    border-bottom: none;
  }

  a:hover {
    text-decoration: none;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 25px;
`;

const Feature = styled.span`
  background: rgba(58, 174, 171, 0.12);
  color: var(--primary-turquoise);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  border: 1px solid rgba(58, 174, 171, 0.25);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const MenuButton = styled.button`
  flex: 1;
  background: var(--primary-turquoise);
  color: var(--navy-blue);
  border: none;
  padding: 12px 20px;
  border-radius: 25px;
  font-family: 'Roboto', 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    width: 100%;
    height: 100%;
    background: rgba(227, 227, 221, 0.2);
    border-radius: 25px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    background: var(--dark-turquoise);
    box-shadow: 0 8px 20px rgba(58, 174, 171, 0.35);
    
    &::before {
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  i {
    margin-right: 8px;
  }
`;

const QRButton = styled.button`
  background: rgba(64, 224, 208, 0.1);
  color: var(--primary-turquoise);
  border: 2px solid var(--primary-turquoise);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    background: var(--primary-turquoise);
    color: var(--navy-blue);
    transform: scale(1.1) rotate(5deg);
  }
`;

const QRModal = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const QRContent = styled.div`
  background: var(--white);
  padding: 30px;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 100%;
  position: relative;
`;

const QRClose = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--navy-blue);
  cursor: pointer;
  
  &:hover {
    color: var(--primary-turquoise);
  }
`;

const QRCode = styled.div`
  width: 200px;
  height: 200px;
  margin: 20px auto;
  background: var(--navy-blue);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: var(--white);
`;

const QRText = styled.p`
  color: var(--navy-blue);
  margin-top: 15px;
  font-weight: 600;
`;

const Locations: React.FC = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const [showQR, setShowQR] = React.useState<string | null>(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    
    // Cleanup previous ScrollTriggers
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.trigger?.classList?.contains('section-header') || 
          trigger.trigger?.classList?.contains('locations-grid') ||
          trigger.trigger === currentRef) {
        trigger.kill();
      }
    });

    // Nota: se quitó la animación del texto (section header) para que no se oculte al hacer scroll.

    gsap.fromTo('.location-card',
      { opacity: 0, y: 30, scale: 0.95 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        ease: 'power2.out',
        stagger: {
          amount: 0.4,
          from: 'start'
        },
        scrollTrigger: {
          trigger: '.locations-grid',
          start: 'top 85%',
          end: 'bottom 15%',
          toggleActions: 'play none none none',
          once: true,
          refreshPriority: -1,
          fastScrollEnd: true,
          preventOverlaps: true
        }
      }
    );

    // Refresh ScrollTrigger after a short delay to ensure proper positioning
    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger?.classList?.contains('section-header') || 
            trigger.trigger?.classList?.contains('locations-grid') ||
            trigger.trigger === currentRef) {
          trigger.kill();
        }
      });
    };
  }, []);

  const handleMenuClick = (locationId: string) => {
    navigate(`/menu/${locationId}`);
  };

  const handleQRClick = (locationId: string) => {
    setShowQR(locationId);
  };

  const closeQR = () => {
    setShowQR(null);
  };

  const currentLocation = showQR ? locations.find(loc => loc.id === showQR) : null;

  return (
    <LocationsSection id="locales" ref={sectionRef}>
      <Container>
        <SectionHeader>
          <SectionTitle>
            Nuestros <span className="highlight">Locales</span>
          </SectionTitle>
          <SectionSubtitle>
            Encuentra la experiencia marina más cerca de ti
          </SectionSubtitle>
        </SectionHeader>

        <LocationsGrid className="locations-grid">
          {locations.map((location) => (
            <LocationCard key={location.id} className="location-card">
              <LocationImageContainer>
                <LazyImage 
                  src={location.image}
                  alt={location.name}
                  height={220}
                  objectFit="cover"
                  placeholder="fas fa-map-marker-alt"
                />
              </LocationImageContainer>
              <LocationContent>
                <LocationName>{location.name}</LocationName>
                  <LocationDistrict>{location.district}</LocationDistrict>
                <LocationDescription>
                  {location.description}
                </LocationDescription>
                
                <LocationDetails>
                  <DetailItem>
                    <i className="fas fa-map-marker-alt"></i>
                    {location.mapUrl ? (
                      <a
                        href={location.mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Abrir ubicación en Google Maps: ${location.address}`}
                      >
                        {location.address}
                      </a>
                    ) : (
                      location.address
                    )}
                  </DetailItem>
                  <DetailItem>
                    <i className="fas fa-phone"></i>
                    {location.phone ? (
                      <a
                        href={`tel:${location.phone.replace(/[^\d+]/g, '')}`}
                        aria-label={`Llamar a ${location.phone}`}
                      >
                        {location.phone}
                      </a>
                    ) : (
                      location.phone
                    )}
                  </DetailItem>
                  <DetailItem>
                    <i className="fas fa-clock"></i>
                    {location.hours}
                  </DetailItem>
                </LocationDetails>

                <FeaturesList>
                  {location.features.map((feature) => (
                    <Feature key={feature}>{feature}</Feature>
                  ))}
                </FeaturesList>

                <ActionButtons>
                  <MenuButton onClick={() => handleMenuClick(location.id)}>
                    <i className="fas fa-utensils"></i>
                    Ver Carta
                  </MenuButton>
                  <QRButton onClick={() => handleQRClick(location.id)}>
                    <i className="fas fa-qrcode"></i>
                  </QRButton>
                </ActionButtons>
              </LocationContent>
            </LocationCard>
          ))}
        </LocationsGrid>
      </Container>

      <QRModal show={showQR !== null} onClick={closeQR}>
        <QRContent onClick={(e) => e.stopPropagation()}>
          <QRClose onClick={closeQR}>
            <i className="fas fa-times"></i>
          </QRClose>
          <h3 style={{ color: 'var(--navy-blue)', marginBottom: '10px' }}>
            Código QR - Carta Digital
          </h3>
          <p style={{ color: 'var(--navy-blue)', fontSize: '0.9rem', marginBottom: '20px' }}>
            {currentLocation?.name}
          </p>
          <QRCode>
            <i className="fas fa-qrcode"></i>
          </QRCode>
          <QRText>
            Escanea para acceder a nuestra carta digital y hacer tu pedido
          </QRText>
          <p style={{ color: 'var(--navy-blue)', fontSize: '0.8rem', marginTop: '10px' }}>
            URL: {window.location.origin}/menu/{showQR}
          </p>
        </QRContent>
      </QRModal>
    </LocationsSection>
  );
};

export default Locations;